import { ApiResult, HttpClient } from "../http.js";
import {
  CompanyFinancialsDto,
  EmployeeEarningsDto,
  ProjectLaborCostDto,
  TradeEarningsDto,
} from "../types/financials.js";

export interface EarningsRangeQuery {
  /** Inclusive range start, resolved client-side (YYYY-MM-DD). */
  start: string;
  /** Inclusive range end, resolved client-side (YYYY-MM-DD). */
  end: string;
  /** The original period label ("day" | "week" | "month" | "custom"), echoed back in the DTO. */
  period: string;
}

export class FinancialsApi {
  constructor(private readonly http: HttpClient) {}

  /** Company-wide financial rollup. Org-scoped server-side; `year` defaults to current year. */
  getCompanyFinancials(year?: number): Promise<ApiResult<CompanyFinancialsDto>> {
    return this.http.get<CompanyFinancialsDto>("/api/companies/financials", {
      query: { year: year ?? null },
    });
  }

  /**
   * Aggregate labour cost (Σ hours × salary-derived hourly cost) for a single project.
   * Computed server-side so individual employee salaries never reach the client.
   */
  getProjectLaborCost(projectId: string): Promise<ApiResult<ProjectLaborCostDto>> {
    return this.http.get<ProjectLaborCostDto>(`/api/projects/${projectId}/labor-cost`);
  }

  /** Earnings for a single employee over a resolved date range. Returns `null` if not a member. */
  getEmployeeEarnings(
    userId: string,
    query: EarningsRangeQuery,
  ): Promise<ApiResult<EmployeeEarningsDto | null>> {
    return this.http.get<EmployeeEarningsDto | null>("/api/companies/earnings/employee", {
      query: {
        userId,
        start: query.start,
        end: query.end,
        period: query.period,
      },
    });
  }

  /** Aggregated earnings by trade/team over a resolved date range. */
  getTradeEarnings(query: EarningsRangeQuery): Promise<ApiResult<TradeEarningsDto[]>> {
    return this.http.get<TradeEarningsDto[]>("/api/companies/earnings/trade", {
      query: {
        start: query.start,
        end: query.end,
        period: query.period,
      },
    });
  }
}
